import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { bindInventoryLiveUpdates } from './inventory-live-updates';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { CurrentContextService } from '../../core/context/current-context.service';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { InventoryPageHintComponent } from './inventory-page-hint.component';
import { CatalogOffering, CatalogService, InventorySupplyOrder } from './catalog.service';

@Component({
  standalone: true,
  selector: 'ain-inventory-supply-order-detail',
  imports: [AinPageComponent, AinStateComponent, InventoryPageHintComponent, RouterLink, TableModule],
  template: `
    <ain-page [title]="t('catalog.supplyOrder.title')" [description]="order()?.referenceNumber || ''">
      <ain-inventory-page-hint pageKey="supplyOrderDetail" />
      @if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else if (!order()) {
        <ain-state type="empty" [title]="t('catalog.supplyOrder.notFound')" />
      } @else {
        <section class="head">
          <a [routerLink]="['/inventory/requisitions', order()!.sourceRequisitionId]">{{ t('catalog.supplyOrder.backToRequisition') }}</a>
          <span>{{ t('columns.status') }}: <strong>{{ statusLabel(order()!.status) }}</strong></span>
        </section>
        <p-table [value]="order()!.lines">
          <ng-template pTemplate="header">
            <tr>
              <th>{{ t('catalog.inventory.offeringId') }}</th>
              <th>{{ t('catalog.supplyOrder.orderedQty') }}</th>
              <th>{{ t('catalog.requisition.estUnitCost') }}</th>
              <th>{{ t('catalog.requisition.estTotalCost') }}</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-line>
            <tr>
              <td>{{ offeringLabel(line.offeringId) }}</td>
              <td>{{ line.orderedQuantity }}</td>
              <td>{{ line.estimatedUnitCost ?? '—' }}</td>
              <td>{{ line.estimatedTotalCost ?? '—' }}</td>
            </tr>
          </ng-template>
        </p-table>
      }
    </ain-page>
  `,
  styles: [`.head { display: flex; gap: 12px; align-items: center; margin-bottom: 12px; }`]
})
export class InventorySupplyOrderDetailComponent implements OnInit {
  private readonly service = inject(CatalogService);
  private readonly route = inject(ActivatedRoute);
  private readonly localization = inject(LocalizationService);
  private readonly liveUpdateService = inject(LiveUpdateService);
  private readonly contextService = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);

  readonly loading = signal(true);
  readonly order = signal<InventorySupplyOrder | null>(null);
  readonly offerings = signal<CatalogOffering[]>([]);

  t(key: string): string { return this.localization.translate(key); }
  offeringLabel(id: string): string {
    const o = this.offerings().find(x => x.id === id);
    return o ? `${o.code} — ${o.title}` : id;
  }

  ngOnInit(): void {
    bindInventoryLiveUpdates(
      { liveUpdateService: this.liveUpdateService, contextService: this.contextService, destroyRef: this.destroyRef },
      () => void this.load(false)
    );
    this.service.listOfferings().subscribe({ next: o => this.offerings.set(o) });
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => void this.load());
  }

  statusLabel(status: string): string {
    const key = `catalog.supplyOrder.status.${status.charAt(0).toLowerCase()}${status.slice(1)}`;
    const translated = this.t(key);
    return translated === key ? status : translated;
  }

  private load(showLoading = true): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (!id) return;
    if (showLoading) this.loading.set(true);
    this.service.getInventorySupplyOrder(id).subscribe({
      next: o => { this.order.set(o); this.loading.set(false); },
      error: () => { this.order.set(null); this.loading.set(false); }
    });
  }
}
